import { config } from '../config.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Set by the auth provider; requests carry it as a bearer token. */
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skips the Authorization header for public endpoints. */
  anonymous?: boolean;
}

/**
 * Registered by the session module. The dependency is inverted rather than
 * imported because `session.ts` imports `apiRequest`, and importing back
 * would close a cycle. Resolves true when a usable token now exists.
 *
 * Must tolerate concurrent invocation. Pages that fetch in parallel — see
 * `pages/admin/Usage.tsx` and `pages/quizzes/QuizResults.tsx` — will have
 * every one of those requests 401 together on the same stale token, so this
 * handler is called several times at once. Collapsing that burst into a
 * single refresh is `session.ts`'s job; this module deliberately does not
 * dedupe, because it cannot know when two calls mean one session.
 */
let onUnauthorized: (() => Promise<boolean>) | null = null;

export function setUnauthorizedHandler(handler: (() => Promise<boolean>) | null) {
  onUnauthorized = handler;
}

/** Reads `authToken` at call time, so a replay picks up a renewed token. */
async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!options.anonymous && authToken) headers.Authorization = `Bearer ${authToken}`;

  try {
    return await fetch(`${config.apiBaseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach FloorPoll. Check your connection.');
  }
}

async function toResult<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof payload.error === 'string' ? payload.error : 'error',
      typeof payload.message === 'string' ? payload.message : 'Something went wrong',
      payload.details,
    );
  }

  return payload as T;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  // A 401 normally means the id token aged out while the tab was asleep.
  // Renew once and replay; a second 401 is a real authorization failure, so
  // it falls through and throws rather than looping.
  //
  // 401 specifically, never `!response.ok`: a 500 can come back from a request
  // the server partially applied, and replaying that would submit a mutating
  // request twice.
  //
  // Anonymous requests are excluded because `/auth/refresh` is itself
  // anonymous, so renewing here would call it recursively — that, rather than
  // "an anonymous request has no token to renew", is what the clause is for.
  if (response.status === 401 && !options.anonymous && onUnauthorized) {
    if (await onUnauthorized()) return toResult<T>(await send(path, options));
  }

  return toResult<T>(response);
}

/** Whether a bearer token is currently held. Telemetry checks this so a
 * signed-out tab does not fire requests that can only ever 401. */
export const hasAuthToken = () => authToken !== null;

/**
 * Fire-and-forget POST for telemetry. Never throws, never replays.
 *
 * Not `apiRequest`, and the differences are the point:
 *
 * - `keepalive` lets the request outlive the page, which is what makes a flush
 *   on `pagehide` land at all. `sendBeacon` would do the same but cannot set
 *   an Authorization header, and the endpoint is authenticated.
 * - No 401 replay. A telemetry flush firing during a tab-restore would race
 *   every other request into `onUnauthorized`, and a background counter is not
 *   worth a token refresh.
 * - No throw. A caller in a click handler must not be able to fail a user's
 *   action because a metric did not record.
 */
export async function apiBeacon(path: string, body: unknown): Promise<void> {
  if (!authToken) return;
  try {
    await fetch(`${config.apiBaseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // Offline, or the page went away mid-flight. Nothing to do and nothing to
    // tell the user: the counts are already lost, and a retry would need a
    // buffer that outlives the page.
  }
}
